import React, { useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Eye, Edit } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ScratchCardApi, ScratchCardConfig } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { CreateScratchCardForm } from "./CreateScratchCardForm";

export function GamificationAllScratchCards() {
  const [scratchCards, setScratchCards] = useState<ScratchCardConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedScratchCard, setSelectedScratchCard] = useState<ScratchCardConfig | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchScratchCards();
  }, []);

  const fetchScratchCards = async () => {
    setIsLoading(true);
    try {
      const response = await ScratchCardApi.getAllScratchCardConfig({});
      
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        const cards = response.scratchCard || [];
        setScratchCards(cards);
      }
    } catch (error: any) {
      console.error("Error fetching scratch cards:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch scratch cards",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "-";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy");
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "-";
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const getRewardTypeLabel = (rewardType: number) => {
    return rewardType === 0 ? "Points" : "Cash";
  };

  const handleView = (card: ScratchCardConfig) => {
    setSelectedScratchCard(card);
    setIsViewModalOpen(true);
  };

  const handleEdit = (card: ScratchCardConfig) => {
    setSelectedScratchCard(card);
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">All Scratch Cards</h2>
          <p className="text-sm text-muted-foreground">
            View and manage all scratch card configurations
          </p>
        </div>
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
        >
          <Plus className="h-4 w-4" />
          Create Scratch Card
        </Button>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <DataTable
          title=""
          data={scratchCards}
          cellClassName="py-6 text-gray-700"
          columns={[
            {
              header: "ID",
              accessorKey: "id" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => row.id.toString(),
            },
            {
              header: "Name",
              accessorKey: "name" as keyof ScratchCardConfig,
            },
            {
              header: "Start Date",
              accessorKey: "startDate" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => formatDate(row.startDate),
            },
            {
              header: "End Date",
              accessorKey: "endDate" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => formatDate(row.endDate),
            },
            {
              header: "Reward Type",
              accessorKey: "rewardType" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => getRewardTypeLabel(row.rewardType),
            },
            {
              header: "Small Reward",
              accessorKey: "smallReward" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => row.smallReward.toString(),
            },
            {
              header: "Medium Reward",
              accessorKey: "mediumReward" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => row.mediumReward.toString(),
            },
            {
              header: "Big Reward",
              accessorKey: "bigReward" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => row.bigReward.toString(),
            },
            {
              header: "Status",
              accessorKey: "isActive" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => (
                <Badge
                  variant={row.isActive ? "default" : "secondary"}
                  className="flex items-center gap-1 w-fit"
                >
                  {row.isActive ? "Active" : "Inactive"}
                </Badge>
              ),
            },
            {
              header: "Created Date",
              accessorKey: "createdDatetime" as keyof ScratchCardConfig,
              cell: (row: ScratchCardConfig) => formatDateTime(row.createdDatetime),
            },
            {
              header: "Action",
              id: "action",
              cell: (row: ScratchCardConfig) => (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleView(row)}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View
                </Button>
              ),
            },
          ]}
        />
      )}

      {/* View Scratch Card Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scratch Card Details</DialogTitle>
          </DialogHeader>
          {selectedScratchCard && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">ID</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedScratchCard.id}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Name</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedScratchCard.name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Start Date</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedScratchCard.startDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">End Date</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedScratchCard.endDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Reward Type</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {getRewardTypeLabel(selectedScratchCard.rewardType)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className="text-sm text-gray-900 mt-1">
                    <Badge
                      variant={selectedScratchCard.isActive ? "default" : "secondary"}
                      className="flex items-center gap-1 w-fit"
                    >
                      {selectedScratchCard.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Small Reward</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedScratchCard.smallReward}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Medium Reward</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedScratchCard.mediumReward}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Big Reward</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedScratchCard.bigReward}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Created Date</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDateTime(selectedScratchCard.createdDatetime)}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </Button>
            {selectedScratchCard && (
              <Button
                type="button"
                className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
                onClick={() => handleEdit(selectedScratchCard)}
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Scratch Card Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Scratch Card</DialogTitle>
          </DialogHeader>
          <CreateScratchCardForm
            initialData={selectedScratchCard}
            isEditMode={true}
            onSuccess={() => {
              setIsEditModalOpen(false);
              setSelectedScratchCard(null);
              // Refresh scratch card list after updating
              fetchScratchCards();
            }}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedScratchCard(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Create Scratch Card Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Scratch Card</DialogTitle>
          </DialogHeader>
          <CreateScratchCardForm
            onSuccess={() => {
              setIsCreateModalOpen(false);
              // Refresh scratch card list after creating
              fetchScratchCards();
            }}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
